
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Order {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  customer_address: string;
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  notes: string;
  created_at: string;
}

export default function OrdersView() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      // Actualizar el estado local
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus }
          : order
      );
      setOrders(updatedOrders);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error al actualizar el estado del pedido');
    }
  };

  const deleteOrder = async (orderId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este pedido?')) return;

    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;

      // Actualizar el estado local
      const updatedOrders = orders.filter(order => order.id !== orderId);
      setOrders(updatedOrders);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error al eliminar el pedido');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesSearch = 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone.includes(searchTerm) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.includes(searchTerm);
    
    return matchesStatus && matchesSearch;
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status: Order['status']) => {
    const statusMap = {
      pending: { text: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: 'ri-time-line' },
      confirmed: { text: 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: 'ri-check-line' },
      shipped: { text: 'Enviado', color: 'bg-purple-100 text-purple-800', icon: 'ri-truck-line' },
      delivered: { text: 'Entregado', color: 'bg-green-100 text-green-800', icon: 'ri-check-double-line' },
      cancelled: { text: 'Cancelado', color: 'bg-red-100 text-red-800', icon: 'ri-close-line' }
    };
    return statusMap[status];
  };

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    totalRevenue: orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + o.total, 0)
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#C6A45C] animate-spin mb-4"></i>
          <p className="text-gray-400">Cargando pedidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
          Gestión de Pedidos
        </h2>
        <button 
          onClick={loadOrders}
          className="bg-[#C6A45C] text-black px-4 py-2 rounded-lg hover:bg-[#B8965A] transition-colors font-medium flex items-center space-x-2 whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          <span>Actualizar</span>
        </button>
      </div>

      {/* Order Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Total</p>
            <p className="text-2xl font-bold text-white">{orderStats.total}</p>
          </div>
        </div>
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-500">{orderStats.pending}</p>
          </div>
        </div>
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Confirmados</p>
            <p className="text-2xl font-bold text-blue-500">{orderStats.confirmed}</p>
          </div>
        </div>
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Enviados</p>
            <p className="text-2xl font-bold text-purple-500">{orderStats.shipped}</p>
          </div>
        </div>
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Entregados</p>
            <p className="text-2xl font-bold text-green-500">{orderStats.delivered}</p>
          </div>
        </div>
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-400">Ingresos</p>
            <p className="text-lg font-bold text-[#C6A45C]">{formatPrice(orderStats.totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Buscar pedido
            </label>
            <div className="relative">
              <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300"
                placeholder="ID, nombre, teléfono o email..."
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Estado
            </label>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 pr-8"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmado</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.map((order) => {
          const statusInfo = getStatusInfo(order.status);
          return (
            <div key={order.id} className="bg-gray-900 rounded-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Pedido #{order.id.slice(0, 8)}</h3>
                  <p className="text-gray-400 text-sm">{formatDate(order.created_at)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                    <i className={`${statusInfo.icon} mr-1`}></i>
                    {statusInfo.text}
                  </span>
                  <select 
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order.id, e.target.value as Order['status'])}
                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:border-[#C6A45C] focus:outline-none pr-8"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="confirmed">Confirmado</option>
                    <option value="shipped">Enviado</option>
                    <option value="delivered">Entregado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-medium mb-2">Información del Cliente</h4>
                  <div className="space-y-1 text-sm text-gray-300">
                    <p><strong>Nombre:</strong> {order.customer_name}</p>
                    <p><strong>Teléfono:</strong> {order.customer_phone}</p>
                    <p><strong>Email:</strong> {order.customer_email}</p>
                    <p><strong>Dirección:</strong> {order.customer_address}</p>
                    {order.notes && <p><strong>Notas:</strong> {order.notes}</p>}
                  </div>
                </div>

                <div>
                  <h4 className="text-white font-medium mb-2">Productos</h4>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-300">
                          {item.name} x{item.quantity}
                        </span>
                        <span className="text-[#C6A45C] font-medium">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between font-bold">
                        <span className="text-white">Total:</span>
                        <span className="text-[#C6A45C] text-lg">{formatPrice(order.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                <div className="flex space-x-2">
                  <a 
                    href={`https://wa.me/${order.customer_phone.replace(/[^0-9]/g, '')}?text=Hola ${order.customer_name}, tu pedido #${order.id.slice(0, 8)} ha sido actualizado.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all duration-300 flex items-center space-x-2 text-sm whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>
                    <span>Contactar</span>
                  </a>
                  <a 
                    href={`mailto:${order.customer_email}?subject=Actualización de pedido #${order.id.slice(0, 8)}`}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center space-x-2 text-sm whitespace-nowrap"
                  >
                    <i className="ri-mail-line"></i>
                    <span>Email</span>
                  </a>
                </div>
                <button 
                  onClick={() => deleteOrder(order.id)}
                  className="text-red-400 hover:text-red-300 transition-colors duration-300"
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-file-list-line text-6xl text-gray-600 mb-4"></i>
          <p className="text-gray-400 text-lg">No se encontraron pedidos</p>
        </div>
      )}
    </div>
  );
}
